import { RefObject } from "react"
import { BANKS, CURRENCIES_CODES, PAYMENT_METHODS } from "./Constants"
import { DocumentAutoGeneratedNumberApi, DocumentAutoGeneratedNumberDataType } from "@/api/DocumentAutoGeneratedNumberApi"
import { ApiSchema } from "@/api/ApiClient"
import moment from "moment"

export const generatePaymentMethodOptions = (paymentMethod: Array<keyof typeof PAYMENT_METHODS> = []) => {
    if (paymentMethod.length == 0) {
        return Object.entries(PAYMENT_METHODS).reduce((a, [k, ob]) => {
            return {
                ...a,
                k: ob.label
            }
        }, {})
    }
    return Object.entries(PAYMENT_METHODS).reduce((a, [k, ob]) => {
        const key = Number(k) as keyof typeof PAYMENT_METHODS;
        if (paymentMethod.includes(key)) {
            return {
                ...a,
                [k]: ob.label
            }
        }
        return a;

    }, {})
}

export const getBankNameLabel = (bankId: string | number) => {
    const id = Number(bankId) as keyof typeof BANKS;
    return BANKS[id];

}

export const getPaymentMethodLabel = (paymentMethodId: string | undefined | null): string => {
    const key = Number(paymentMethodId);
    const paymentMethod = PAYMENT_METHODS[key];
    return paymentMethod.label;
}

export const goNextOnEnter = (e: React.KeyboardEvent<HTMLInputElement>, ref: RefObject<HTMLInputElement>) => {
    if (e.key === 'Enter') {
        e.preventDefault()
        e.stopPropagation()
        goToField(ref)
    }
}

export const goToField = (ref: RefObject<HTMLInputElement>) => {
    ref.current?.select()
    ref.current?.focus()
}

export const generateSelectOption = (option: Record<string, string>): SelectOptionType[] => {
    return Object.entries(option).map(([categoryId, label]) => {
        return {
            label: label,
            value: categoryId
        }
    })
}

export const generateNextNumberByModel = async (modelName: keyof ApiSchema): Promise<DocumentAutoGeneratedNumberDataType> => {
    let generatedNumber = await DocumentAutoGeneratedNumberApi.getAutoGeneratedNumberByModelName(modelName)
    if (generatedNumber?.id) {
        let { number } = generatedNumber;
        return {
            ...generatedNumber,
            number: Number(number || 0) + 1
        }
    } else {
        generatedNumber = {
            modelName: modelName,
            number: 1,
        }
    }
    return generatedNumber
}

export const isEmpty = (v: unknown) =>
    v == null || (typeof v === "string" && v.trim() === "") || (Array.isArray(v) && v.length === 0);

export const toNumberFormat = (number: string | number): string => {
    const formattedNumber = Number(number || 0)
    return formattedNumber.toFixed(2)
}

export const stripHtmlTags = (html: string) => {
    const div = document.createElement('div');
    div.innerHTML = html;
    return div.textContent || div.innerText || '';
}

const escapeHtml = (text: string): string => {
    return text
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;")
        .replace(/'/g, "&#39;");
}

/**
 * Convierte controles de formulario (textarea/input/select/button) presentes en un
 * fragmento HTML en texto plano dentro de un <div>, preservando los saltos de linea
 * del usuario como <br>. Util al inyectar HTML guardado en vistas de impresion/PDF,
 * donde un <textarea> se renderiza como control real (bordes, scroll, font-size del
 * user-agent) en lugar de texto.
 */
export const unwrapFormControlsToText = (html: string): string => {
    if (!html) return html;
    const doc = new DOMParser().parseFromString(html, "text/html");

    const buildPreformatted = (text: string): HTMLDivElement => {
        const div = doc.createElement("div");
        div.className = "invoice-info-preformatted";
        div.innerHTML = escapeHtml(text).replace(/\r\n|\r|\n/g, "<br>");
        return div;
    }

    doc.querySelectorAll("textarea").forEach((el) => {
        const value = (el as HTMLTextAreaElement).value ?? el.textContent ?? "";
        el.replaceWith(buildPreformatted(value));
    });

    doc.querySelectorAll("input").forEach((el) => {
        const value = (el as HTMLInputElement).value ?? "";
        el.replaceWith(buildPreformatted(value));
    });

    doc.querySelectorAll("select, button").forEach((el) => {
        el.replaceWith(buildPreformatted(el.textContent ?? ""));
    });

    return doc.body.innerHTML;
}

export const chunkArray = <T>(array: T[], size: number): T[][] => {
    const result: T[][] = [];
    for (let i = 0; i < array.length; i += size) {
        result.push(array.slice(i, i + size));
    }
    return result;
};

export type Removable<T, P extends readonly PropertyKey[]> = Omit<T, Extract<P[number], keyof T>>;

/**
 * Elimina propiedades de un objeto según la lista indicada.
 * 
 * @param obj - Objeto original
 * @param propsToRemove - Lista de propiedades a eliminar
 *  * @param removeNulls - Si es true, también eliminará propiedades con valor null
 * @returns Nuevo objeto sin las propiedades indicadas
 */
export function omitProperties<
    T extends object,
    P extends readonly (keyof T | string)[]
>(
    obj: T,
    propsToRemove: P,
    removeNulls = false
): Removable<T, P> {
    const keys = Object.keys(obj) as (keyof T)[];
    const removeSet = new Set<keyof T>(
        propsToRemove.filter((k): k is keyof T => (keys as readonly string[]).includes(k as string))
    );

    const out: any = {};
    for (const [k, v] of Object.entries(obj) as [keyof T, T[keyof T]][]) {
        if (removeSet.has(k)) continue;
        if (removeNulls && v === null) continue;
        out[k] = v;
    }
    return out;
}

/**
 * Compara dos listas de atributos y devuelve
 * los que están en `compareWith` pero no en `base`.
 *
 * @param base - Array de atributos de referencia
 * @param compareWith - Array de atributos a comparar
 * @returns Lista de atributos faltantes en `base`
 */
export const getMissingAttributes = (
    base: readonly string[],
    compareWith: readonly string[]
): string[] => {
    const baseSet = new Set(base);
    return compareWith.filter(key => !baseSet.has(key));
};

export const removeFunctionFromObject = <T extends object>(
    obj: T
): T => {
    return Object.entries(obj).reduce((a, [key, value]) => {
        if (typeof value != "function") {
            return {
                ...a,
                [key]: value
            }
        }
        return {
            ...a
        }

    }, {} as T)
};

/**
 * Busca recursivamente una propiedad en un objeto JSON
 * @param obj - Objeto JSON donde buscar
 * @param keyToFind - Nombre de la propiedad que querés encontrar
 * @returns El valor de la propiedad si existe, o null
 */
export const findPropertyRecursive = (obj: any, keyToFind: string): any => {
    if (obj == null || typeof obj !== "object") return null;

    if (obj.hasOwnProperty(keyToFind)) {
        return obj[keyToFind];
    }

    for (const key of Object.keys(obj)) {
        const value = obj[key];
        if (typeof value === "object") {
            const result = findPropertyRecursive(value, keyToFind);
            if (result !== null && result !== undefined) {
                return result;
            }
        }
    }

    return null;
};

export const safeEncodeBase64 = (data: string): string => {
    const bytes = new TextEncoder().encode(data); // convierte a UTF-8
    let binary = "";
    bytes.forEach(b => binary += String.fromCharCode(b));
    return btoa(binary);
}

export interface SelectOptionType {
    label: string;
    value: string;
}

/**
 * Devuelve el primer y último día de un mes específico.
 * @param year Año en formato numérico (ej. 2025)
 * @param month Mes en formato numérico (1-12)
 * @returns Objeto con startDate y endDate en formato ISO (YYYY-MM-DD)
 */
export const getMonthBoundaries = (yearInput: string | number, monthInput: string | number, useFirstMonthDate: boolean = true) => {
    const year = Number(yearInput)
    const month = Number(monthInput)

    const startDate = moment({ year, month: month - 1 }).startOf("month").format("YYYY-MM-DD");
    let endDate;
    if (useFirstMonthDate) {
        // primer día del mes siguiente (en vez del último día del mes actual)
        endDate = moment({ year, month: month - 1 })
            .add(1, "month")
            .startOf("month")
            .format("YYYY-MM-DD");
    } else {
        endDate = moment({ year, month: month - 1 })
            .endOf("month")
            .format("YYYY-MM-DD");
    }

    return { startDate, endDate };
};

export const getYearBoundaries = (yearInput: string | number, useFirstMonthDate: boolean = true) => {
    const year = Number(yearInput)

    const startDate = moment({ year, month: 0 }).startOf("month").format("YYYY-MM-DD");
    let endDate;
    if (useFirstMonthDate) {
        endDate = moment({ year, month: 11 })
            .add(1, "month")
            .startOf("month")
            .format("YYYY-MM-DD");
    } else {
        endDate = moment({ year, month: 11 })
            .endOf("month")
            .format("YYYY-MM-DD");
    }

    return { startDate, endDate };
};
